// Import all CSS styles
import '../styles/app.css';

import CONFIG from './config/config.js';
import { initBackgroundRotator } from './background-rotator.js';
import { initLandingEffects } from './landing-effects.js';

/**
 * WHAT: Core Application Module
 * WHY: Provides base functionality and environment detection
 */

// Initialize configuration if not already done
if (!window.CONFIG) {
    window.CONFIG = {
        api: CONFIG.api,
        features: CONFIG.features
    };
}

// Environment management singleton
const EnvironmentManager = {
    _instance: null,
    _environment: null,

    getInstance() {
        if (!this._instance) {
            this._instance = this;
            this._environment = CONFIG.environment;
        }
        return this._instance;
    },

    getEnvironment() {
        return this._environment;
    }
};

// Initialize environment manager once when the script loads
const envManager = EnvironmentManager.getInstance();

function getCurrentPage() {
    const path = window.location.pathname;
    const pageName = path.split('/').pop().replace('.html', '');
    return pageName || 'index';
}

function runWhenIdle(task, timeout = 1500) {
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(task, { timeout });
    } else {
        window.setTimeout(task, 0);
    }
}

function isAdminPage(pathname) {
    return /\/admin(-|\.html|$)/.test(pathname) || pathname.endsWith('/admin');
}

async function loadPageSpecificModules() {
    const pathname = window.location.pathname || '';
    const deferredImports = [];

    if (isAdminPage(pathname)) {
        deferredImports.push(import('./admin.js'));
    } else {
        // Deferred import is OK — tracker bootstraps even if DOMContentLoaded already fired.
        runWhenIdle(() => {
            import('./analytics-tracker.js')
                .then((mod) => {
                    if (typeof mod.bootstrapAnalyticsTracker === 'function') {
                        mod.bootstrapAnalyticsTracker();
                    }
                })
                .catch((error) => {
                    console.warn('Failed to load analytics tracker:', error);
                });
        }, 300);
    }

    if (deferredImports.length > 0) {
        await Promise.all(deferredImports);
    }
}

// Debug helper for network requests
const debugNetworkRequest = async (url) => {
    try {
        const response = await fetch(url, { 
            method: 'HEAD',
            credentials: 'same-origin'
        });
        return response.ok;
    } catch (error) {
        console.error(`Failed to check resource ${url}:`, error);
        return false;
    }
};

// Function to try loading the image
const loadBackgroundImage = async (imgPath, element) => {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            element.style.backgroundImage = `url('${imgPath}')`;
            element.classList.add('loaded');
            resolve(true);
        };
        img.onerror = (error) => {
            console.error("Failed to load image:", error);
            console.error("Full image URL attempted:", new URL(imgPath, window.location.origin).href);
            console.error("Image element state:", {
                complete: img.complete,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                currentSrc: img.currentSrc
            });

            // Try to fetch the image directly to see if the issue is with the Image() constructor
            fetch(imgPath)
                .then(async (response) => {
                    const responseClone = response.clone();
                    try {
                        await responseClone.blob();
                    } catch (error) {
                        console.error("Failed to get image blob:", error);
                    }
                })
                .catch(error => {
                    console.error("Image fetch error:", error);
                });

            resolve(false);
        };
        // Set the image source to trigger loading
        img.src = imgPath;
    });
};

// Initialize app and handle background image
const initApp = async () => {
    // Check for maintenance mode
    if (CONFIG.features.maintenanceMode) {
        document.body.innerHTML = `
            <div class="maintenance-mode">
                <h1>Site Maintenance</h1>
                <p>We're currently updating our site. Please check back soon.</p>
            </div>
        `;
        return;
    }

    await loadPageSpecificModules();

    // Landing skips body background rotation; interior pages defer rotator so API/menu
    // requests win Safari's limited per-host connection pool on first paint.
    const homeSlideshow =
        document.body.classList.contains('home')
            ? import('./slideshow.js').then((m) => {
                  if (typeof m.initHomePageSlideshows === 'function') {
                      return m.initHomePageSlideshows();
                  }
                  return Promise.resolve();
              })
            : Promise.resolve();

    const rotatorTask = document.body.classList.contains('home')
        ? initBackgroundRotator()
        : new Promise((resolve) => {
              window.setTimeout(() => {
                  initBackgroundRotator().finally(resolve);
              }, 350);
          });

    await Promise.all([rotatorTask, homeSlideshow]);

    if (typeof window !== 'undefined' && window.location.search.includes('rotateNow=1')) {
        const u = new URL(window.location.href);
        u.searchParams.delete('rotateNow');
        const clean = u.pathname + (u.search || '') + (u.hash || '');
        window.history.replaceState(null, '', clean);
    }

    // Keep scroll/parallax setup just off the critical path.
    runWhenIdle(() => {
        initLandingEffects();
    }, 700);
};

// Initialize when DOM is ready, or immediately if already ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    // DOM is already ready, run immediately
    initApp();
}

// Handle API errors globally
window.addEventListener('unhandledrejection', (event) => {
    if (CONFIG.features.debugLogging) {
        console.error('Unhandled promise rejection:', event.reason);
    }
    // Show user-friendly error message
    const errorContainer = document.createElement('div');
    errorContainer.className = 'error-notification';
    errorContainer.textContent = 'Something went wrong. Please try again later.';
    document.body.appendChild(errorContainer);
    setTimeout(() => errorContainer.remove(), 5000);
});

// Cleanup intervals on page unload
window.addEventListener('beforeunload', () => {
    if (window.parallaxRotationInterval) {
        clearInterval(window.parallaxRotationInterval);
        window.parallaxRotationInterval = null;
    }
});

export { getCurrentPage, initApp, EnvironmentManager };